import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getUserNotifications, 
  markNotificationAsRead,
  filterDisplayableNotifications,
  getNotificationById
} from '../utils/notificationUtils';
import { useAuth } from '../contexts/AuthContext';
import type { Notification } from '../types/notification';

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Use ref to store the latest fetchNotifications function to avoid stale closures
  const fetchNotificationsRef = useRef<() => Promise<void>>(async () => {});

  // Fetch notifications  
  const fetchNotifications = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      console.log('🔔 useNotifications: Fetching notifications for user:', user.uid, 'email:', user.email);
      
      const userNotifications = await getUserNotifications(user.uid, {}, user.email || undefined);
      console.log('🔔 useNotifications: getUserNotifications returned', userNotifications.length, 'notifications (already filtered)');
      
      // NOTE: getUserNotifications already applies filterDisplayableNotifications, so we don't need to filter again
      setNotifications(userNotifications);
      console.log('🔔 useNotifications: Set state with', userNotifications.length, 'notifications');
      console.log('🔔 useNotifications: Unread count:', userNotifications.filter(n => !n.isRead && !n.read_at).length);
    } catch (err) {
      console.error('🔔 useNotifications: Error fetching notifications:', err);
      setError('Failed to fetch notifications');
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [user]);
  
  // Update ref whenever fetchNotifications changes
  useEffect(() => {
    fetchNotificationsRef.current = fetchNotifications;
  }, [fetchNotifications]);

  // Subscribe to event-driven notification updates
  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    console.log('🔔 useNotifications: Setting up event listener for user:', user.uid);
    
    // Initial fetch
    fetchNotifications();

    // Event listener for push notifications
    const handleNotificationReceived = async (event: Event) => {
      console.log('🔔🔔🔔 EVENT RECEIVED: notification-received', {
        timestamp: Date.now(),
        event,
        hasRef: !!fetchNotificationsRef.current
      });

      // Use ref to get most current version of fetchNotifications
      if (!fetchNotificationsRef.current) {
        console.error('❌ fetchNotificationsRef.current is null!');
        return;
      }

      // If pushId is provided in event detail, try to fetch that single notification
      try {
        const custom = (event as CustomEvent<any>);
        const pushId = custom?.detail?.pushId;
        if (pushId) {
          try {
            console.log('🔔 Received pushId in event detail, fetching single notification:', pushId);
            const single = await getNotificationById(pushId);
            if (single) {
              // Merge into current notifications if not present and displayable
              setNotifications(prev => {
                const exists = prev.some(n => n.id === single.id);
                if (exists) return prev;
                const merged = [single, ...prev];
                return filterDisplayableNotifications(merged);
              });
              console.log('✅ Merged single notification from pushId into state');
            } else {
              console.log('🔔 Notification doc not found for pushId, will fallback to full fetch');
            }
          } catch (err) {
            console.error('❌ Error fetching single notification by pushId:', err);
          }
        }
      } catch (err) {
        console.error('❌ Error reading event.detail for pushId:', err);
      }

      // Immediate fetch, then retry after short delays to handle eventual consistency
      const tryFetch = async () => {
        try {
          console.log('🔔 Calling fetchNotifications (immediate) from ref...');
          await fetchNotificationsRef.current();
          console.log('✅ fetchNotifications completed (immediate)');
        } catch (err) {
          console.error('❌ fetchNotifications error (immediate):', err);
        }
      };

      // Schedule retries to allow Firestore write to propagate if push arrived before DB write
      const scheduleRetries = () => {
        const retryDelays = [800, 2000]; // ms
        retryDelays.forEach((delay, idx) => {
          setTimeout(async () => {
            try {
              console.log(`🔁 retry #${idx + 1} fetching notifications after ${delay}ms`);
              await fetchNotificationsRef.current();
              console.log(`✅ retry #${idx + 1} fetch completed`);
            } catch (err) {
              console.error(`❌ retry #${idx + 1} fetch error:`, err);
            }
          }, delay);
        });
      };

      tryFetch();
      scheduleRetries();
    };
    
    console.log('🔔 useNotifications: Registering notification-received listener');
    window.addEventListener('notification-received', handleNotificationReceived as EventListener);

    return () => {
      console.log('🔔 useNotifications: Removing notification-received listener');
      window.removeEventListener('notification-received', handleNotificationReceived as EventListener);
    };
  }, [user, fetchNotifications]);

  const handleNotificationClick = useCallback(async (notification: Notification, fromPanel?: boolean) => {
    console.log('🔔 useNotifications: handleNotificationClick called with:', {
      notificationId: notification.id,
      type: notification.type,
      data: notification.data
    });

    // Check if notification has valid ID
    if (!notification.id) {
      console.error('🔔 useNotifications: Notification has no ID, cannot mark as read');
      return;
    }

    // Mark as read first
    try {
      console.log('🔔 useNotifications: Marking notification as read...');
      await markNotificationAsRead(notification.id);
      console.log('🔔 useNotifications: Successfully marked as read');
      
      // Update local state optimistically
      setNotifications(prev => 
        prev.map(n => 
          n.id === notification.id ? { ...n, read_at: new Date() } : n
        )
      );
    } catch (error) {
      console.error('🔔 useNotifications: Error marking notification as read:', error);
    }

    // Handle navigation based on notification type
    try {
      // PRIORITY 1: Check notification type first for sales orders
      // This ensures sales order notifications always go to the approver page,
      // even if they have old/incorrect link fields
      if (notification.type === 'sales_order_approver' ||
          notification.type === 'sales_order_fyi' ||
          notification.type === 'sales_order_created' || 
          notification.type === 'sales_order_updated') {
        const salesOrderId = notification.data?.salesOrderId;
        if (salesOrderId) {
          console.log('🔔 useNotifications: Sales order notification, navigating to /sales-order/' + salesOrderId);
          navigate(`/sales-order/${salesOrderId}`, { state: { fromNotifications: true } });
          return;
        }
      }

      // PRIORITY 2: Use the link field from notification if available (set by backend)
      // This handles other notification types (reminders, etc.)
      if (notification.link && notification.link !== '/notifications/' && notification.link.startsWith('/')) {
        console.log('🔔 useNotifications: Navigating to notification.link:', notification.link);
        navigate(notification.link);
        return;
      }

      // PRIORITY 3: Default fallback - navigate to notification detail page
      // Include state when coming from panel.
      if (notification.id) {
        if (fromPanel) {
          navigate(`/notifications/${notification.id}`, { state: { fromPanel: true } });
        } else {
          navigate(`/notifications/${notification.id}`);
        }
      } else {
        console.warn('🔔 useNotifications: Notification has no id, cannot navigate to details');
      }
    } catch (error) {
      console.error('🔔 useNotifications: Navigation error:', error);
    }
  }, [navigate]);

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await markNotificationAsRead(notificationId);
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId ? { ...n, read_at: new Date() } : n
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.read_at);
      
      await Promise.all(
        unreadNotifications.map(n => n.id && markNotificationAsRead(n.id))
      );
      
      setNotifications(prev => 
        prev.map(n => ({ ...n, read_at: new Date() }))
      );
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, [notifications]);

  const unreadCount = notifications.filter(n => !n.isRead && !n.read_at).length;
  
  console.log('🔔 [useNotifications] Current state:', {
    totalNotifications: notifications.length,
    unreadCount,
    loading,
  });

  return {
    notifications,
    loading,
    error,
    unreadCount,
    handleNotificationClick,
    markAsRead,
    markAllAsRead,
    refetch: fetchNotifications,
  };
};
